import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import AdminAuth from './components/AdminAuth';
import AdminSidebar from './components/AdminSidebar';
import SystemMetrics from './components/SystemMetrics';
import UserManagement from './components/UserManagement';
import PartnerManagement from './components/PartnerManagement';
import FraudMonitoring from './components/FraudMonitoring';
import ContentManagement from './components/ContentManagement';
import SubscriptionManagement from './components/SubscriptionManagement';

export default function AdminManagementDashboard() {
  const [adminUser, setAdminUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('metrics');

  useEffect(() => {
    // Initial session check
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      if (session?.user) {
        const role = session?.user?.raw_user_meta_data?.role ||
          session?.user?.user_metadata?.role ||
          session?.user?.app_metadata?.role;
        if (role === 'admin') {
          setAdminUser(session?.user);
        }
      }
      setAuthLoading(false);
    });

    // Real-time auth state listener
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        setAdminUser(null);
        setAuthLoading(false);
        return;
      }
      if (session?.user) {
        const role = session?.user?.raw_user_meta_data?.role ||
          session?.user?.user_metadata?.role ||
          session?.user?.app_metadata?.role;
        setAdminUser(role === 'admin' ? session?.user : null);
      }
      setAuthLoading(false);
    });

    return () => subscription?.unsubscribe();
  }, []);

  const handleSignOut = async () => {
    setAuthLoading(true);
    try {
      await supabase?.auth?.signOut();
    } catch (e) {
      console.error('Sign out error:', e);
    } finally {
      setAdminUser(null);
      setAuthLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0D1B2A' }}>
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#7B2FBE', borderTopColor: 'transparent' }} />
          <p className="text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Verifying access...</p>
        </div>
      </div>
    );
  }

  if (!adminUser) {
    return <AdminAuth onAuthSuccess={(user) => setAdminUser(user)} />;
  }

  const sectionComponents = {
    metrics: <SystemMetrics />,
    users: <UserManagement />,
    partners: <PartnerManagement />,
    fraud: <FraudMonitoring />,
    content: <ContentManagement />,
    subscriptions: <SubscriptionManagement />,
  };

  return (
    <div className="flex min-h-screen" style={{ background: '#0D1B2A' }}>
      <AdminSidebar
        activeSection={activeSection}
        onSectionChange={setActiveSection}
        onSignOut={handleSignOut}
      />
      <main className="flex-1 overflow-y-auto">
        <div className="p-6 lg:p-8">
          {/* Header */}
          <div className="mb-6 pb-5" style={{ borderBottom: '1px solid rgba(0,229,255,0.08)' }}>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Admin Management Dashboard</h1>
                <p className="text-sm mt-1" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>FinVisAR Platform Administration Console</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-xs font-medium text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>{adminUser?.user_metadata?.full_name || adminUser?.email}</p>
                  <p className="text-xs" style={{ color: '#7B2FBE', fontFamily: 'DM Sans, sans-serif' }}>Super Admin</p>
                </div>
                <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold text-sm" style={{ background: 'linear-gradient(135deg, #7B2FBE, #00E5FF)' }}>
                  {(adminUser?.user_metadata?.full_name || adminUser?.email || 'A')?.charAt(0)?.toUpperCase()}
                </div>
                <button
                  onClick={handleSignOut}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200"
                  style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#EF4444', fontFamily: 'DM Sans, sans-serif' }}
                >
                  Sign Out
                </button>
              </div>
            </div>
          </div>

          {/* Section Content */}
          {sectionComponents?.[activeSection] || <SystemMetrics />}
        </div>
      </main>
    </div>
  );
}
